import { supabase } from '@/integrations/supabase/client';

import { createCRUDEntity } from './crud-factory';
import { transformEquipmentFromDB, EquipmentType } from './Equipment';

// Application-level ProviderEquipment type (transformed from DB)
export type ProviderEquipmentType = {
  id: string;
  providerId: string;
  equipmentId: string;
  installationDate: string | null;
  installationYear: number | null;
  lastUpgradeYear: number | null;
  quantity: number | null;
  availabilityStatus: string | null;
  locationInClinic: string | null;
  lastMaintenanceDate: string | null;
  nextMaintenanceDate: string | null;
  maintenanceProvider: string | null;
  maintenanceNotes: string | null;
  isFlagship: boolean | null;
  displayPriority: number | null;
  notesInternal: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

const transformFromDB = (data: unknown): ProviderEquipmentType => {
  if (!data) {
    throw new Error('Cannot transform null/undefined data');
  }
  const dataRecord = data as Record<string, unknown>;

  // Helper function to safely convert values to strings
  const safeString = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    return ''; // Return empty string for objects/arrays/functions
  };

  return {
    id: safeString(dataRecord.id),
    providerId: safeString(dataRecord.provider_id),
    equipmentId: safeString(dataRecord.equipment_id),
    installationDate: dataRecord.installation_date as string | null,
    installationYear: dataRecord.installation_year as number | null,
    lastUpgradeYear: dataRecord.last_upgrade_year as number | null,
    quantity: dataRecord.quantity as number | null,
    availabilityStatus: dataRecord.availability_status as string | null,
    locationInClinic: dataRecord.location_in_clinic as string | null,
    lastMaintenanceDate: dataRecord.last_maintenance_date as string | null,
    nextMaintenanceDate: dataRecord.next_maintenance_date as string | null,
    maintenanceProvider: dataRecord.maintenance_provider as string | null,
    maintenanceNotes: dataRecord.maintenance_notes as string | null,
    isFlagship: dataRecord.is_flagship as boolean | null,
    displayPriority: dataRecord.display_priority as number | null,
    notesInternal: dataRecord.notes_internal as string | null,
    createdAt: dataRecord.created_at as string | null,
    updatedAt: dataRecord.updated_at as string | null,
  };
};

const transformToDB = (data: unknown) => {
  const dataRecord = data as Record<string, unknown>;
  const dbData = {
    provider_id: dataRecord.providerId,
    equipment_id: dataRecord.equipmentId,
    installation_date: dataRecord.installationDate,
    installation_year: dataRecord.installationYear,
    last_upgrade_year: dataRecord.lastUpgradeYear,
    quantity: dataRecord.quantity,
    availability_status: dataRecord.availabilityStatus,
    location_in_clinic: dataRecord.locationInClinic,
    last_maintenance_date: dataRecord.lastMaintenanceDate,
    next_maintenance_date: dataRecord.nextMaintenanceDate,
    maintenance_provider: dataRecord.maintenanceProvider,
    maintenance_notes: dataRecord.maintenanceNotes,
    is_flagship: dataRecord.isFlagship,
    display_priority: dataRecord.displayPriority,
    notes_internal: dataRecord.notesInternal,
  };

  return Object.fromEntries(Object.entries(dbData).filter(([, v]) => v !== undefined));
};

const transformConfig = {
  transformFromDB,
  transformToDB,
};

interface ProviderEquipmentWithDetails {
  [key: string]: unknown;
  equipment: EquipmentType;
}

export const ProviderEquipment = createCRUDEntity<'provider_equipment', ProviderEquipmentType>(
  'provider_equipment',
  transformConfig,
  {
    customMethods: {
      // Custom method to get equipment for a provider with joined data
      getByProvider: async (providerId: string): Promise<ProviderEquipmentWithDetails[]> => {
        const { data, error } = await supabase
          .from('provider_equipment')
          .select(
            `
            *,
            equipment:equipment_id (*)
          `
          )
          .eq('provider_id', providerId)
          .order('display_priority', { ascending: true })
          .order('equipment(name)', { ascending: true });

        if (error) throw error;
        return data.map(item => ({
          ...(transformFromDB(item) as Record<string, unknown>),
          equipment: transformEquipmentFromDB(item.equipment),
        }));
      },
    },
  }
);
export default ProviderEquipment;
